import React, { FC } from 'react'
import cx from 'classnames'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { makeStyles } from 'tss-react/mui'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import RadioGroup from '@mui/material/RadioGroup'
import FormHelperText from '@mui/material/FormHelperText'
import Button from '@mui/material/Button'
import { Checkbox, RadioButton, TextField } from '../atoms'
import { theme } from '../../lib/styles/universal'
import {
    computePricingQuote,
    InterestedOrganizationCommunitySize,
    InterestedOrganizationDTO,
    InterestedOrganizationReferralSource,
    InterestedOrganizationSubscriptionType,
    INTEREST_COMMUNITY_SIZE_OPTIONS,
    INTEREST_REFERRAL_SOURCE_OPTIONS,
    INTEREST_SUBSCRIPTION_OPTIONS,
} from '@hazadapt-git/public-core-base'
import {
    formatInterestPricingAmount,
    INTEREST_FORM_DEFAULT_COMMUNITY_SIZE,
} from '../../lib/utils/organization'

/** SLA linked from the interest form; confirm URL with marketing/legal if needed. */
const RESILIENCEPOINT_SLA_URL =
    'https://www.hazadapt.com/terms/rp-service-level-agreement'

type InterestFormValues = Omit<
    InterestedOrganizationDTO,
    'community_size' | 'referral_source'
> & {
    community_size: InterestedOrganizationCommunitySize | ''
    referral_source: InterestedOrganizationReferralSource | ''
}

const DEFAULT_VALUES: InterestFormValues = {
    community_size: '',
    contact_name: '',
    official_email: '',
    organization_name: '',
    jurisdiction_served: '',
    website_url: '',
    referral_source: '',
    details: '',
    subscription_type: InterestedOrganizationSubscriptionType.MONTHLY,
    organization_representative: false,
    local_content_interest: false,
}

const validationSchema = Yup.object().shape({
    community_size: Yup.string().required('Community size is required'),
    contact_name: Yup.string().required('Contact name is required'),
    official_email: Yup.string()
        .email('Invalid email address')
        .required('Official email is required'),
    organization_name: Yup.string().required('Organization name is required'),
    jurisdiction_served: Yup.string().required(
        'Jurisdiction served is required'
    ),
    website_url: Yup.string()
        .matches(
            /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
            'Invalid website URL'
        )
        .required('Website URL is required'),
    referral_source: Yup.string().optional(),
    details: Yup.string().optional(),
    subscription_type: Yup.string().required(
        'Please select a subscription type'
    ),
    organization_representative: Yup.boolean().oneOf(
        [true],
        'Please confirm you represent this organization'
    ),
    local_content_interest: Yup.boolean().optional(),
})

interface InterestFormProps {
    onSubmit: (values: InterestedOrganizationDTO) => Promise<void> | void
    initialValues?: Partial<InterestFormValues>
    submitLabel?: string
}

export const InterestForm: FC<InterestFormProps> = ({
    onSubmit,
    initialValues,
    submitLabel = 'Submit Request',
}) => {
    const { classes: localClasses } = useLocalStyles()
    const formInitialValues = {
        ...DEFAULT_VALUES,
        ...initialValues,
    }

    return (
        <Formik<InterestFormValues>
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (values, formikHelpers) => {
                const validationErrors = await formikHelpers.validateForm()
                if (Object.keys(validationErrors).length > 0) {
                    // Touch all fields to show errors
                    const touchedFields = Object.keys(values).reduce(
                        (acc, key) => ({ ...acc, [key]: true }),
                        {}
                    )
                    formikHelpers.setTouched(touchedFields)
                    formikHelpers.setSubmitting(false)
                    return
                }
                try {
                    const { referral_source, details, community_size, ...rest } =
                        values
                    await onSubmit({
                        ...rest,
                        community_size:
                            community_size as InterestedOrganizationCommunitySize,
                        ...(referral_source ? { referral_source } : {}),
                        ...(details ? { details } : {}),
                    })
                } catch (err) {
                    console.error(err)
                } finally {
                    formikHelpers.setSubmitting(false)
                }
            }}
        >
            {({
                values,
                handleChange,
                handleBlur,
                touched,
                errors,
                setFieldValue,
                isSubmitting,
            }) => (
                <Form className={localClasses.form}>
                    <Stack gap="2rem">
                        <div className={localClasses.section}>
                            <Typography
                                variant="h4"
                                className={localClasses.sectionTitle}
                            >
                                Choose your community size:
                            </Typography>
                            <TextField
                                name="community_size"
                                select
                                label="Community Size *"
                                value={values.community_size}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Select Community Size"
                                error={
                                    touched.community_size &&
                                    Boolean(errors.community_size)
                                }
                                helperText={
                                    touched.community_size
                                        ? errors.community_size
                                        : ''
                                }
                                SelectProps={{
                                    displayEmpty: true,
                                }}
                            >
                                <MenuItem value="" disabled>
                                    Select Community Size
                                </MenuItem>
                                {INTEREST_COMMUNITY_SIZE_OPTIONS.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                        <div className={localClasses.section}>
                            <Typography
                                variant="h4"
                                className={localClasses.sectionTitle}
                            >
                                Tell us about yourself/your organization:
                            </Typography>
                            <Grid
                                container
                                spacing={2}
                                className={localClasses.gridSection}
                            >
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="contact_name"
                                        label="Contact Name *"
                                        placeholder="Contact Name"
                                        value={values.contact_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.contact_name &&
                                            Boolean(errors.contact_name)
                                        }
                                        helperText={
                                            touched.contact_name
                                                ? errors.contact_name
                                                : ''
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="official_email"
                                        label="Official Email *"
                                        placeholder="Official Email"
                                        value={values.official_email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.official_email &&
                                            Boolean(errors.official_email)
                                        }
                                        helperText={
                                            touched.official_email
                                                ? errors.official_email
                                                : ''
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="organization_name"
                                        label="Organization Name *"
                                        placeholder="Organization Name"
                                        value={values.organization_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.organization_name &&
                                            Boolean(errors.organization_name)
                                        }
                                        helperText={
                                            touched.organization_name
                                                ? errors.organization_name
                                                : ''
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="jurisdiction_served"
                                        label="Jurisdiction Served *"
                                        placeholder="City/County/Tribal?"
                                        value={values.jurisdiction_served}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.jurisdiction_served &&
                                            Boolean(errors.jurisdiction_served)
                                        }
                                        helperText={
                                            touched.jurisdiction_served
                                                ? errors.jurisdiction_served
                                                : ''
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="website_url"
                                        label="Website or Organization Link *"
                                        placeholder="https://"
                                        value={values.website_url}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.website_url &&
                                            Boolean(errors.website_url)
                                        }
                                        helperText={
                                            touched.website_url
                                                ? errors.website_url
                                                : ''
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="referral_source"
                                        label="How did you hear about us?"
                                        select
                                        placeholder="Select an option"
                                        value={values.referral_source}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={
                                            touched.referral_source &&
                                            Boolean(errors.referral_source)
                                        }
                                        helperText={
                                            touched.referral_source
                                                ? errors.referral_source
                                                : ''
                                        }
                                        SelectProps={{
                                            displayEmpty: true,
                                        }}
                                    >
                                        <MenuItem value="">
                                            Select an option
                                        </MenuItem>
                                        {INTEREST_REFERRAL_SOURCE_OPTIONS.map(
                                            (option) => (
                                                <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            )
                                        )}
                                    </TextField>
                                </Grid>
                            </Grid>
                            <TextField
                                name="details"
                                label="Tell us more about your organization"
                                placeholder="Share any details that will help us serve your community."
                                value={values.details}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                    touched.details && Boolean(errors.details)
                                }
                                helperText={
                                    touched.details ? errors.details : ''
                                }
                                multiline
                                minRows={4}
                            />
                        </div>
                        <FormControl
                            component="fieldset"
                            className={localClasses.section}
                            error={
                                touched.subscription_type &&
                                Boolean(errors.subscription_type)
                            }
                        >
                            <Typography
                                variant="h4"
                                className={localClasses.sectionTitle}
                            >
                                Choose your subscription type:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">ResiliencePoint is a recurring subscription billed monthly or annually, as selected during purchase. You may cancel at any time before your next renewal date to stop future renewal charges, and access will continue through the end of your current paid term.</Typography>
                            <RadioGroup
                                name="subscription_type"
                                value={values.subscription_type}
                                onChange={(event) =>
                                    setFieldValue(
                                        'subscription_type',
                                        event.target.value
                                    )
                                }
                                className={localClasses.planGroup}
                            >
                                {INTEREST_SUBSCRIPTION_OPTIONS.map((option) => {
                                    const isSelected =
                                        values.subscription_type ===
                                        option.value
                                    const pricingCommunity =
                                        values.community_size ||
                                        INTEREST_FORM_DEFAULT_COMMUNITY_SIZE
                                    const tierQuote =
                                        option.value !==
                                        InterestedOrganizationSubscriptionType.DEMO
                                            ? computePricingQuote(
                                                  pricingCommunity,
                                              )
                                            : null
                                    const selectedAmount =
                                        tierQuote &&
                                        (option.value ===
                                        InterestedOrganizationSubscriptionType.YEARLY
                                            ? tierQuote.yearly
                                            : tierQuote.monthly)
                                    const quoteDisplay = selectedAmount
                                        ? formatInterestPricingAmount(
                                              selectedAmount,
                                          )
                                        : null
                                    return (
                                        <Paper
                                            key={option.value}
                                            elevation={isSelected ? 4 : 1}
                                            className={cx(
                                                localClasses.planCard,
                                                {
                                                    [localClasses.planCardSelected]:
                                                        isSelected,
                                                },
                                            )}
                                            onClick={() =>
                                                setFieldValue(
                                                    'subscription_type',
                                                    option.value,
                                                )
                                            }
                                        >
                                            <RadioButton
                                                value={option.value}
                                                label={
                                                    <Stack gap="0.25rem">
                                                        <Typography variant="h4">
                                                            {option.title}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {option.subtitle}
                                                        </Typography>
                                                        {quoteDisplay ? (
                                                            <Typography
                                                                variant="h5"
                                                                component="p"
                                                                fontWeight={700}
                                                                sx={{
                                                                    mt: 0.25,
                                                                }}
                                                            >
                                                                {quoteDisplay}
                                                            </Typography>
                                                        ) : null}
                                                    </Stack>
                                                }
                                                containerStyle={{
                                                    alignItems: 'flex-start',
                                                    width: '100%',
                                                }}
                                            />
                                        </Paper>
                                    )
                                })}
                            </RadioGroup>
                            {touched.subscription_type &&
                                typeof errors.subscription_type ===
                                    'string' && (
                                    <FormHelperText>
                                        {errors.subscription_type}
                                    </FormHelperText>
                                )}
                        </FormControl>
                        <div className={localClasses.checkboxGroup}>
                            <Checkbox
                                name="organization_representative"
                                checked={values.organization_representative}
                                onChange={(_, checked) =>
                                    setFieldValue(
                                        'organization_representative',
                                        checked
                                    )
                                }
                                label={
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        color="text.primary"
                                    >
                                        I confirm that I am authorized to act
                                        on behalf of this organization, that
                                        ResiliencePoint will be used to support
                                        public resilience, and that I agree to
                                        the{' '}
                                        <Link
                                            href={RESILIENCEPOINT_SLA_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            onClick={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            ResiliencePoint Service Level
                                            Agreement
                                        </Link>{' '}
                                        and billing terms.
                                    </Typography>
                                }
                            />
                            {touched.organization_representative &&
                                typeof errors.organization_representative ===
                                    'string' && (
                                    <FormHelperText
                                        error
                                        className={localClasses.checkboxError}
                                    >
                                        {errors.organization_representative}
                                    </FormHelperText>
                                )}
                            <Checkbox
                                name="local_content_interest"
                                checked={values.local_content_interest}
                                onChange={(_, checked) =>
                                    setFieldValue(
                                        'local_content_interest',
                                        checked
                                    )
                                }
                                label="I am also interested in hosting my alert links and other Local Safety Information on HazAdapt."
                            />
                        </div>
                        <div className={localClasses.actions}>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={isSubmitting}
                            >
                                {submitLabel}
                            </Button>
                        </div>
                    </Stack>
                </Form>
            )}
        </Formik>
    )
}

const useLocalStyles = makeStyles()({
    form: {
        width: '100%',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
    },
    sectionTitle: {
        fontWeight: 600,
    },
    gridSection: {
        marginTop: '0 !important',
    },
    planGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
    },
    planCard: {
        borderRadius: '0.75rem',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        padding: '1rem 1.25rem',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        display: 'flex',
        alignItems: 'center',
    },
    planCardSelected: {
        borderColor: theme.palette.primary.main,
    },
    checkboxGroup: {
        display: 'grid',
        gap: '0.75rem',
    },
    checkboxError: {
        marginLeft: '2.5rem',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
    },
})
